import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Stack, Box, CircularProgress, Alert, Typography, TextField, Button, useTheme } from "@mui/material";
import { Gift, CheckCircle } from "lucide-react";
import axios from "axios";
import PageProtectedOnboarding from "../../../components/Page/PageProtectedOnboarding";
import { pool, USERDATA } from "../../../constants";
import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";
import { redeemGift } from "../../../Auth/auth";
import OnbSteps from "../../../components/Steps/OnbSteps";
import ButtonForm from "../../../components/Button/ButtonForm";

// Maps a failed POST /api/gifts/redeem/<code>/ to a translation key (status + `error` string).
function redeemErrorKey(err) {
  const status = err?.response?.status;
  const error = err?.response?.data?.error;
  if (status === 409 && error === "Not eligible for gift redemption") return "onbgift_error_not_eligible";
  if (status === 409 && error === "You already have an active subscription.") return "onbgift_error_active_subscription";
  if (status === 404) return "onbgift_error_not_found";
  if (status === 400 && error === "Already redeemed") return "onbgift_error_already_redeemed";
  if (status === 400 && error === "Not payable/found") return "onbgift_error_not_payable";
  if (status === 400 && error === "Redemption window expired") return "onbgift_error_expired";
  if (status === 429) return "onbgift_error_rate_limit";
  return "onbgift_error_generic";
}

export default function OnbGiftCode() {
  const { state, handleLogin, handleLogout } = useAuth();
  const { t, language, changeLanguage } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (state.user?.language) {
      changeLanguage(state.user.language);
    }
  }, [state.user?.language]);

  const handleSubmit = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setError(null);
    setLoading(true);
    try {
      const data = await redeemGift(trimmed);
      // Refresh the user so onboarding_status = 2 is reflected before routing on.
      try {
        const userRes = await axios.get(USERDATA);
        handleLogin({ user: userRes.data, token: state.token });
      } catch (refreshErr) {
        console.error("User refresh after gift redemption failed:", refreshErr);
        handleLogin({ user: { ...state.user, onboarding_status: data.onboarding_status }, token: state.token });
      }
      setResult(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        navigate("/Login");
        return;
      }
      setError(t(redeemErrorKey(err)));
    } finally {
      setLoading(false);
    }
  };

  const accentColor = theme.palette.tertiary.C300;
  const cardSx = {
    border: '1px solid',
    borderRadius: 4,
    overflow: 'hidden',
    borderLeft: `10px solid ${accentColor}`,
    p: { xs: 2.5, sm: 4 },
    ...(theme.palette.mode === 'light'
      ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
      : { bgcolor: 'greydark.C980', borderColor: 'greydark.C400',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
  };

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(language, { year: "numeric", month: "long", day: "numeric" });

  return (
    <PageProtectedOnboarding
      headerImage={pool}
      brandtext={t("onbsub_brandtext")}
      content={
        <>
          <Grid container sx={{ mb: 5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Grid size={12} sx={{ width: '100%', maxWidth: 900, px: { xs: 1, sm: 2 }, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <OnbSteps stepNumber={result ? 1 : 0} step1Label={t("onbgift_step1")} />
            </Grid>
          </Grid>

          <Box sx={{ width: '100%', maxWidth: 600, px: { xs: 1, sm: 2 }, mx: 'auto', mb: 6 }}>
            <Box sx={cardSx}>
              {result ? (
                <Stack spacing={2} alignItems="center" textAlign="center">
                  <CheckCircle size={40} color={theme.palette.traffic.green} />
                  <Typography variant="h6" fontWeight={700}>{t("onbgift_success_title")}</Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t("onbgift_success_text").replace("{{date}}", formatDate(result.expires_at))}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{t("onbgift_success_no_cost")}</Typography>
                  <ButtonForm name={t("onbgift_btn_continue")} onClick={() => navigate("/FirstLabvisit", { replace: true })} />
                </Stack>
              ) : (
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                      bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Gift size={20} color={accentColor} />
                    </Box>
                    <Typography variant="h6" fontWeight={500}>{t("onbgift_step1")}</Typography>
                  </Stack>

                  <TextField
                    label={t("onbgift_field_label")}
                    helperText={t("onbgift_field_helper")}
                    value={code}
                    onChange={(e) => { setCode(e.target.value); setError(null); }}
                    onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                    disabled={loading}
                    fullWidth
                    autoFocus
                    autoComplete="off"
                    slotProps={{ htmlInput: { maxLength: 32, style: { textTransform: 'uppercase', letterSpacing: 1 } } }}
                  />

                  {error && <Alert severity="error">{error}</Alert>}

                  <ButtonForm
                    name={loading ? <CircularProgress size={18} color="inherit" /> : t("onbgift_btn_submit")}
                    onClick={handleSubmit}
                    disabled={loading || !code.trim()}
                  />
                </Stack>
              )}
            </Box>

            {!result && (
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">{t("onbgift_no_code")}</Typography>
                <Button variant="text" size="small" onClick={() => navigate("/OnbSubscription")}>
                  {t("onbgift_btn_back")}
                </Button>
              </Stack>
            )}
          </Box>
        </>
      }
    />
  );
}
